import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, IconButton, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem
} from '@mui/material';
import { ArrowBack, Add, LockOpen, Lock, Delete, DateRange } from '@mui/icons-material';
import axios from 'axios';
import { locale } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmt = (d) => d
  ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const PayrollPeriodList = () => {
  const navigate = useNavigate();
  const [periods,  setPeriods]  = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [genDlg,       setGenDlg]       = useState(false);
  const [genYear,      setGenYear]      = useState(new Date().getFullYear().toString());
  const [saving,       setSaving]       = useState(false);
  const [deletePeriodDlg, setDeletePeriodDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/payroll/periods`, { headers: hdrs() });
      setPeriods(res.data);
    } catch { setError('Failed to load periods'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/payroll/periods/generate`, { year: genYear }, { headers: hdrs() });
      setSuccess(`${genYear} periods generated`); setGenDlg(false); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Generate failed'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (period) => {
    const newStatus = period.status === 'Open' ? 'Closed' : 'Open';
    try {
      await axios.put(`${API}/api/payroll/periods/${period.periodCode}`, { status: newStatus }, { headers: hdrs() });
      setSuccess(`${period.periodName} set to ${newStatus}`); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Update failed'); }
  };

  const confirmDeletePeriod = async () => {
    if (!deletePeriodDlg) return;
    try {
      await axios.delete(`${API}/api/payroll/periods/${deletePeriodDlg.periodCode}`, { headers: hdrs() });
      setSuccess('Deleted'); setDeletePeriodDlg(null); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const years = [];
  for (let y = new Date().getFullYear() + 2; y >= 2022; y--) years.push(y.toString());

  // Group by year
  const grouped = {};
  periods.forEach(p => {
    const yr = p.periodCode.slice(-4);
    if (!grouped[yr]) grouped[yr] = [];
    grouped[yr].push(p);
  });

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/payroll')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <DateRange />
              <Typography variant="h6" fontWeight={700}>Payroll Periods</Typography>
            </Box>
            <Button startIcon={<Add />} variant="contained"
              onClick={() => setGenDlg(true)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              Generate Year
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {Object.keys(grouped).sort((a, b) => b - a).map(yr => (
        <Card key={yr} elevation={2} sx={{ mb: 2 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
              {yr}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell><strong>Start Date</strong></TableCell>
                  <TableCell><strong>End Date</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped[yr].map(p => (
                  <TableRow key={p.periodCode} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.periodName}</Typography>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{p.periodCode}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmt(p.startDate)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmt(p.endDate)}</Typography></TableCell>
                    <TableCell align="center">
                      <Chip label={p.status} size="small"
                        color={p.status === 'Open' ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={p.status === 'Open' ? 'Close Period' : 'Reopen Period'}>
                        <IconButton size="small" onClick={() => toggleStatus(p)}>
                          {p.status === 'Open' ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeletePeriodDlg(p)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      {periods.length === 0 && (
        <Card elevation={1}>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              No payroll periods yet. Click "Generate Year" to create periods for a year.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Delete Period Confirmation Dialog */}
      <Dialog open={Boolean(deletePeriodDlg)} onClose={() => setDeletePeriodDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Payroll Period</DialogTitle>
        <DialogContent>
          <Typography>
            Delete period <strong>{deletePeriodDlg?.periodName}</strong>? All runs linked to this period must be deleted first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePeriodDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeletePeriod}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={genDlg} onClose={() => setGenDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Payroll Periods</DialogTitle>
        <DialogContent dividers>
          <TextField select label="Year" value={genYear} fullWidth size="small"
            onChange={e => setGenYear(e.target.value)} InputLabelProps={{ shrink: true }}>
            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Creates 12 monthly periods (Jan–Dec) for the selected year. Existing periods are not overwritten.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={generate} disabled={saving}
            sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollPeriodList;
